import { useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { Button } from "@sfc/ui";
import { trpc } from "./trpc";
import { useEffectiveUser } from "./hooks/use-effective-user";
import { isIPadOfflineFailure } from "./offline/offlineCache";
import { useNetworkStatus } from "./hooks/useNetworkStatus";
import LoadingSpinner from "./components/utils/LoadingSpinner";
import { RoundRouteProvider } from "./RoundRouteContext";

type Props = {
  children: ReactNode;
  renderFallback: (content: ReactNode) => ReactNode;
};

/** Resolve the round at entry; Wouter owns the one real browser route. */
export function RoundAddressRouter({ children, renderFallback }: Props) {
  const [path] = useLocation();
  const reference = path.match(/^\/round\/([^/]+)(?:\/|$)/)?.[1];
  if (!reference) return children;
  return (
    <RoundAddressEntry
      key={reference}
      reference={reference}
      renderFallback={renderFallback}
    >
      {children}
    </RoundAddressEntry>
  );
}

function RoundAddressEntry({
  reference,
  children,
  renderFallback,
}: Props & { reference: string }) {
  const [roundId, setRoundId] = useState<string | null>(() =>
    reference.startsWith("r-") ? reference : null,
  );
  const { data: user } = useEffectiveUser();
  const { isOnline } = useNetworkStatus();
  const query = trpc.getRoundSlugs.useQuery(
    { viewerId: user?.userId ?? "" },
    {
      enabled: !roundId && !!user && isOnline,
      staleTime: 0,
      refetchOnMount: "always",
      retry: 1,
    },
  );
  if (roundId)
    return (
      <RoundRouteProvider value={{ roundId, reference }}>
        {children}
      </RoundRouteProvider>
    );
  const mayUseCache =
    !isOnline || (query.isError && isIPadOfflineFailure(query.error));
  const ready =
    mayUseCache ||
    (query.isFetchedAfterMount && !query.isFetching && !query.isError);
  const match = ready
    ? query.data?.find((address) => address.slug === reference)
    : undefined;
  if (match) {
    // Same-component state adjustment commits the entry decision before any
    // round consumer mounts. Later viewer/cache changes cannot change its ID.
    setRoundId(match.roundId);
    return null;
  }
  if (!ready && !query.isError) return renderFallback(<LoadingSpinner />);
  return renderFallback(
    <main className="mx-auto max-w-lg p-6 space-y-4">
      <h1>Unable to open this round</h1>
      <p className="text-muted-foreground">
        {query.isError || mayUseCache
          ? "The round address could not be checked. Reconnect and try again, or open the round from your home page."
          : "This address does not exist, or you do not have access to this round."}
      </p>
      <Button variant="outline" onClick={() => void query.refetch()}>
        Try again
      </Button>
      <a className="link ml-4" href="/">
        Go to home page
      </a>
    </main>,
  );
}
